// src/api/action-list.ts
//
// The living action list (backend: app/services/action_engine.py and
// action_list.py). Every action is produced by a generator from a figure in a
// specific read, re-scored on every later read, and confirmed or retired by
// the data. Nothing here is typed by hand except the admin fields.
//
// Counts (answers, present) are only in an ADMIN's payload; the server strips
// them for clients, as it does hidden actions and admin-only fields.

import { apiFetch } from "./api-fetch";
import { downloadExportFile } from "./onboarding-api";

export type ActionStatus = "new" | "carried" | "in_progress" | "confirmed" | "retired";
export type ActionClassificationKind = "new" | "updated" | "carried" | "confirmed" | "not_yet_moved" | "retired";
export type ItemStage = "new" | "tested" | "published" | "measured";
export type ItemKind = "draft" | "page" | "asset" | "section" | "dataset" | "comparison" | "story";

/** One read's verdict on an action, with the read that set it. */
export interface ActionClassification {
  kind: ActionClassificationKind;
  label: string;
  badge: string;
  run_id: number | null;
  read_label: string | null;
  reasons: string[];
  by: "read" | "admin";
}

export interface ActionItem {
  id: number;
  kind: ItemKind;
  title: string;
  url: string | null;
  classification: ItemStage;
  classification_label: string;
  stages: { stage: ItemStage; read_label?: string | null; at?: string | null; after_read?: string | null; verdict?: string | null }[];
  tested: boolean;
  // Admin only:
  wind_tunnel?: { run_id: number; draft_id?: string | null; verdict?: string | null; status?: string | null } | null;
  created_by?: string | null;
  published_by?: string | null;
}
export type ActionEffort = "low" | "medium" | "high";

export interface ActionCell {
  share: number | null;
  directional?: boolean;
  answers?: number;
  present?: number;
}

export interface ActionLane {
  model: string;
  label: string;
}

export interface ActionEvidence {
  run_id: number;
  read_number: number;
  read_label: string;
  generator: string;
  generator_label: string;
  kind?: "remeasure";
  belief: { id: string; name: string } | null;
  statement: string;
  figures: { label: string; cell: ActionCell }[];
  lanes: ActionLane[];
  quote: { text: string; model: string; model_label: string } | null;
  // Generator 6 (truth set): the builds proposed for a wrong fact: the page it
  // should live on, the facts-layer entry, and corrections to request.
  builds?: { kind: "page" | "facts_layer" | "correction" | "own_page"; text: string; url: string | null }[];
  gap?: { slot: string; label: string; truth_value: string; wrong_values: string[] };
}

export interface GapStatus {
  status: "new" | "in_action" | "fixed" | "persisting";
  label: string;
  reads_elapsed?: number;
  read_number?: number;
}

/** Precedents from the cross-client index (#134): resolved publications that
 *  moved a belief of the same rubric category in this client's vertical.
 *  Anonymised; clients get only that they exist. */
export interface ActionPrecedent {
  experimental: boolean;
  has_precedent: boolean;
  count?: number;
  belief_category?: string;
  vertical?: string;
  // Admin only:
  precedents?: Precedent[];
}

export interface Precedent {
  id: number;
  vertical: string;
  asset_type: string;
  intervention_class: string | null;
  intervention_label: string;
  belief_category: string;
  lanes: string[];
  question_types: string[];
  wind_tunnel_delta: number | null;
  outcome: 'moved' | 'reversed';
  lasting?: 'held' | 'faded' | 'pending';
  reads: { reads_after: number; status: string; flag: 'up' | 'down' | null; restarted: boolean; follow_up?: boolean }[];
  figures: Record<string, { before: { share: number | null } | null; after: { share: number | null } | null; delta: number | null }>;
  reads_to_move: number | null;
  days_to_move: number | null;
  published_quarter: string;
  line: string;
}

/** A publication made for this action and what it moved (#134). */
export interface ActionPublicationOutcome {
  publication_id: number;
  title: string;
  published_at: string;
  status: 'not_measured' | 'not_yet' | 'moved' | 'reversed';
  status_label: string;
  resolved: boolean;
  resolved_at_label?: string | null;
  lasting?: 'held' | 'faded' | 'pending' | null;
  lasting_label?: string | null;
  note: string;
}

export interface LivingAction {
  id: number;
  title: string;
  generated_title?: string;
  generator: string;
  generator_label: string;
  belief: { id: string; name: string } | null;
  status: ActionStatus;
  status_label: string;
  status_note: string | null;
  added_in: string | null;
  new_in_read: string | null;
  rank: number | null;
  position?: number;
  impact_score: number | null;
  impact_components?: { headroom: number; conversion: number; reachability: number; trend: number } | null;
  target_now: ActionCell | null;
  effort: ActionEffort | null;
  pinned: boolean;
  hidden?: boolean;
  admin_retired?: boolean;
  superseded_by: number | null;
  evidence: ActionEvidence[];
  latest_evidence: ActionEvidence | null;
  what_it_moves: {
    metric_label?: string;
    lanes_first?: ActionLane[];
    checked_at_label?: string;
  };
  source_models: ActionLane[];
  published: { at: string; after_read: string | null; note: string; url: string | null; published_by?: string } | null;
  confirmation: {
    before: { read_label: string; cell: ActionCell };
    after: { read_label: string; cell: ActionCell };
    delta: number;
    band: number;
    reads_elapsed: number;
  } | null;
  precedent: ActionPrecedent;
  publications?: ActionPublicationOutcome[];
  gap_status?: GapStatus | null;
  classification: ActionClassification;
  classification_history: ActionClassification[];
  lanes: ActionLane[];
  items: ActionItem[];
}

export interface ActionChange {
  kind: "added" | "moved_up" | "moved_down" | "confirmed" | "retired";
  label: string;
  action_id: number | null;
  title: string;
  reason: string;
}

export interface ActionListView {
  reads: { run_id: number; read_number: number; label: string }[];
  as_of: { run_id: number; label: string } | null;
  open: LivingAction[];
  confirmed: LivingAction[];
  retired: LivingAction[];
  changes: ActionChange[];
  /** "What changed in Read N": this read's verdicts; counts for admins only. */
  summary: {
    read_run_id: number | null;
    total?: number;
    by_classification: { kind: ActionClassificationKind; label: string; share: number | null; count?: number }[];
  };
  viewer_is_admin: boolean;
}

export interface ActionEvent {
  id: number;
  run_id: number | null;
  kind: string;
  reason: string;
  detail: Record<string, unknown>;
  actor: string | null;
  created_at: string;
}

const base = (customerId: number) => `/deep-signal/customers/${customerId}/actions`;

export async function getActionList(customerId: number, asOfRunId?: number): Promise<ActionListView> {
  const q = asOfRunId != null ? `?as_of_run_id=${asOfRunId}` : "";
  return apiFetch(`${base(customerId)}${q}`);
}

export async function downloadActionListExport(customerId: number, format: "md" | "json"): Promise<void> {
  return downloadExportFile(
    `${base(customerId)}/export.${format}`,
    `actions_${customerId}.${format}`,
    format === "md" ? "text/markdown" : "application/json",
  );
}

/** Admin: title, effort, pin, hide. Evidence is never editable. */
export async function editAction(
  customerId: number,
  actionId: number,
  patch: Partial<{ title_override: string | null; effort: ActionEffort | null; pinned: boolean; hidden: boolean }>,
): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function retireAction(
  customerId: number,
  actionId: number,
  reason: string,
  supersededBy?: number | null,
): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}/retire`, {
    method: "POST",
    body: JSON.stringify({ reason, superseded_by: supersededBy ?? null }),
  });
}

export async function restoreAction(customerId: number, actionId: number): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}/restore`, { method: "POST" });
}

/** Admin: enter this action into the action log as published, from a
 *  publication record on this client (#133: published is set by a
 *  publication record, never by hand). */
export async function publishAction(
  customerId: number,
  actionId: number,
  publicationId: number,
): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}/publish`, {
    method: "POST",
    body: JSON.stringify({ publication_id: publicationId }),
  });
}

export async function getActionLog(customerId: number, actionId: number): Promise<{ events: ActionEvent[] }> {
  return apiFetch(`${base(customerId)}/${actionId}/log`);
}

/** Admin: a draft, page or asset built for an action. */
export async function addActionItem(
  customerId: number,
  actionId: number,
  item: { kind: ItemKind; title: string; url?: string; wind_tunnel_run_id?: number | null; wind_tunnel_draft_id?: string | null },
): Promise<{ ok: boolean; id: number }> {
  return apiFetch(`${base(customerId)}/${actionId}/items`, { method: "POST", body: JSON.stringify(item) });
}

/** Admin: edit an item, or link the wind tunnel run (and draft) that tested it. */
export async function editActionItem(
  customerId: number,
  actionId: number,
  itemId: number,
  patch: Partial<{ title: string; kind: ItemKind; url: string | null; wind_tunnel_run_id: number | null; wind_tunnel_draft_id: string | null }>,
): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(patch) });
}

/** Admin: the item is live, as a publication record on this client says.
 *  The first one also enters the action into the action log. */
export async function publishActionItem(
  customerId: number,
  actionId: number,
  itemId: number,
  publicationId: number,
): Promise<{ ok: boolean }> {
  return apiFetch(`${base(customerId)}/${actionId}/items/${itemId}/publish`, {
    method: "POST",
    body: JSON.stringify({ publication_id: publicationId }),
  });
}
